import { io, Socket } from 'socket.io-client';
import { Platform } from 'react-native';
import { store, removeActiveJob } from '../store';

const API_URL = process.env.EXPO_PUBLIC_API_URL || 'http://localhost:3000/api';
// Strip /api suffix to get base URL for socket connection
const BASE_URL = API_URL.replace(/\/api$/, '');

let socket: Socket | null = null;
let socketDriverId: string | undefined; // the driverId baked into the live socket's query
let globalHandlersBound = false;

function buildSocket(driverId?: string): Socket {
  socketDriverId = driverId;
  return io(BASE_URL, {
    path: '/ws',
    transports: Platform.OS === 'web' ? ['polling', 'websocket'] : ['websocket', 'polling'],
    autoConnect: false,
    query: driverId ? { driverId } : undefined,
  });
}

function teardownSocket() {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  socketDriverId = undefined;
  globalHandlersBound = false;
}

export function getSocket(): Socket {
  // Pass driverId so the server can join the driver-specific room.
  const driverId = store.getState().auth.driver?.id;
  // Reconcile identity: if the live socket was built with a different (or missing)
  // driverId — cold-boot before rehydrate, or re-login as a DIFFERENT driver — tear it
  // down and rebuild so the server joins the correct driver room. Without this the cached
  // singleton keeps the first/empty driverId forever and every driver-targeted event
  // (job:fleet-assigned, job:status, job:reassigned) silently never arrives.
  if (socket && socketDriverId !== driverId) {
    teardownSocket();
  }
  if (!socket) {
    socket = buildSocket(driverId);
  }
  return socket;
}

export function connectSocket() {
  const s = getSocket();
  if (!s.connected) {
    s.connect();
    // Server auto-joins 'job-board' + driver-specific room on connection
  }
  // Global handler: if this job is reassigned away / removed from this driver,
  // drop it from the active list immediately (no manual refresh needed).
  if (!globalHandlersBound) {
    globalHandlersBound = true;
    const drop = (data: any) => { if (data?.jobId) store.dispatch(removeActiveJob(data.jobId)); };
    s.on('job:removed', drop);
    s.on('job:reassigned', drop);
  }
  return s;
}

export function disconnectSocket() {
  teardownSocket();
}

// Auto-teardown on logout: when the auth token clears, drop the socket so a subsequent
// re-login (possibly as a different driver on a shared device) rebuilds it with the new
// identity instead of reconnecting the previous driver's room.
let lastToken = store.getState().auth.token;
store.subscribe(() => {
  const t = store.getState().auth.token;
  if (lastToken && !t) teardownSocket();
  lastToken = t;
});
